import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Show } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import { LayoutDashboard, Building2, Receipt, Landmark, Wallet, BookOpen, Users, FolderOpen, Headset, CalendarDays, LifeBuoy, Sparkles, CreditCard, Settings as Cog,
  FileInput, Calculator, ListChecks, FileCheck2, CalendarCheck, NotebookPen, BadgeDollarSign, MessagesSquare, BarChart3, UserCog, Briefcase, Layers, Repeat, Plug, Bell, Palette, FileText, ScrollText, ShieldCheck } from "lucide-react";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { SessionProvider, SiteProvider, useSession } from "@/context/Session";
import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import { HowItWorks, Services, Pricing, ForWho, Faq, About, Contact, Terms } from "@/pages/public/Pages";
import { SignInPage, SignUpPage } from "@/pages/AuthPages";
import Onboarding from "@/pages/Onboarding";
import AppShell from "@/components/app/AppShell";
import { Loading } from "@/components/app/ui";
import Dashboard from "@/pages/app/Dashboard";
import { Financial, Invoices, Taxes, Employees, GUIDES, ASSESSMENTS, PERIODS, EMPLOYEES, PAYROLL, TabbedPage } from "@/pages/app/Modules";
import { Documents, DocumentList, REQUESTS } from "@/pages/app/Documents";
import Tickets from "@/pages/app/Tickets";
import Calendar from "@/pages/app/Calendar";
import Assistant from "@/pages/app/Assistant";
import { Company, Accounting, MyAccountant, Subscription, Settings } from "@/pages/app/More";
import { ContadorDashboard, ContadorCompanies, OrgSettings, Reports } from "@/pages/contador/Contador";
import * as A from "@/pages/admin/Admin";

const EMP = [["/app", "Visão Geral", LayoutDashboard], ["/app/financeiro", "Financeiro", Wallet], ["/app/documentos", "Documentos", FolderOpen], ["/app/impostos", "Impostos e Guias", Landmark],
  ["/app/empresa", "Minha Empresa", Building2], ["/app/notas", "Notas Fiscais", Receipt], ["/app/contabilidade", "Contabilidade", BookOpen], ["/app/funcionarios", "Funcionários", Users],
  ["/app/contador", "Meu Contador", Headset], ["/app/calendario", "Calendário", CalendarDays], ["/app/suporte", "Suporte", LifeBuoy], ["/app/assistente", "Normah AI", Sparkles],
  ["/app/relatorios", "Relatórios", BarChart3], ["/app/assinatura", "Minha Assinatura", CreditCard], ["/app/configuracoes", "Configurações", Cog]];
const CONT = [["/contador", "Dashboard", LayoutDashboard], ["/contador/empresas", "Empresas Atendidas", Building2], ["/contador/documentos", "Documentos Recebidos", FileInput], ["/contador/atendimento", "Atendimento", MessagesSquare],
  ["/contador/apuracoes", "Apurações", Calculator], ["/contador/notas", "Notas Fiscais", Receipt], ["/contador/obrigacoes", "Obrigações Fiscais", ListChecks], ["/contador/guias", "Guias de Pagamento", FileCheck2],
  ["/contador/fechamento", "Fechamento Mensal", CalendarCheck], ["/contador/escrituracao", "Escrituração", NotebookPen], ["/contador/folha", "Folha de Pagamento", BadgeDollarSign],
  ["/contador/relatorios", "Relatórios", BarChart3], ["/contador/configuracoes", "Configurações", Cog]];
const ADM = [["/admin", "Dashboard", LayoutDashboard], ["/admin/empresas", "Empresas", Building2], ["/admin/usuarios", "Usuários", UserCog], ["/admin/parceiros", "Contadores Parceiros", Briefcase],
  ["/admin/planos", "Planos", Layers], ["/admin/assinaturas", "Assinaturas e Pagamentos", Repeat], ["/admin/integracoes", "Integrações", Plug], ["/admin/notificacoes", "Notificações", Bell],
  ["/admin/suporte", "Suporte", LifeBuoy], ["/admin/marca", "Configurações de Marca", Palette], ["/admin/conteudo", "Conteúdo do Site", FileText], ["/admin/auditoria", "Logs e Auditoria", ScrollText],
  ["/admin/seguranca", "Segurança", ShieldCheck], ["/admin/relatorios", "Relatórios", BarChart3]];
const menu = (m) => m.map(([to, label, icon]) => ({ to, label, icon }));

function Gate({ children }) {
  return (<><Show when="signed-in">{children}</Show><Show when="signed-out"><Navigate to="/sign-in" replace /></Show></>);
}

function useMe() {
  const { me } = useSession();
  return me;
}

function EmpresaArea() {
  const me = useMe();
  const { companyId, setCompanyId } = useSession();
  const nav = useNavigate();
  const mine = me?.companies?.filter((c) => c.side === "empresa") || [];
  useEffect(() => {
    if (!me || me.error) return;
    if (!mine.length) nav(me.orgs?.length ? "/contador" : me.user?.platform_role ? "/admin" : "/onboarding", { replace: true });
    else if (!mine.some((c) => c.id === companyId)) setCompanyId(mine[0].id);
  }, [me]); // eslint-disable-line
  if (!me || !mine.some((c) => c.id === companyId)) return <div className="p-8"><Loading /></div>;
  return <AppShell menu={menu(EMP)} side="empresa" title="Empresa" needsCompany />;
}

function ContadorArea() {
  const me = useMe();
  const { orgId, setOrgId } = useSession();
  const nav = useNavigate();
  useEffect(() => {
    if (!me || me.error) return;
    if (!me.orgs?.length) nav("/app", { replace: true });
    else if (!me.orgs.some((o) => o.id === orgId)) setOrgId(me.orgs[0].id);
  }, [me]); // eslint-disable-line
  if (!me || !me.orgs?.some((o) => o.id === orgId)) return <div className="p-8"><Loading /></div>;
  return <AppShell menu={menu(CONT)} side="contador" title="Contador" needsCompany />;
}

function AdminArea() {
  const me = useMe();
  if (!me) return <div className="p-8"><Loading /></div>;
  if (!me.user?.platform_role) return <Navigate to="/app" replace />;
  return <AppShell menu={menu(ADM)} side="admin" title="Admin" />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} /><Route path="/como-funciona" element={<HowItWorks />} /><Route path="/servicos" element={<Services />} />
        <Route path="/planos" element={<Pricing />} /><Route path="/para-quem" element={<ForWho />} /><Route path="/faq" element={<Faq />} />
        <Route path="/sobre" element={<About />} /><Route path="/contato" element={<Contact />} /><Route path="/termos" element={<Terms />} />
      </Route>
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />
      <Route path="/onboarding" element={<Gate><Onboarding /></Gate>} />
      <Route path="/app" element={<Gate><EmpresaArea /></Gate>}>
        <Route index element={<Dashboard />} /><Route path="empresa" element={<Company />} /><Route path="notas" element={<Invoices />} /><Route path="impostos" element={<Taxes />} />
        <Route path="financeiro" element={<Financial />} /><Route path="contabilidade" element={<Accounting />} /><Route path="funcionarios" element={<Employees />} />
        <Route path="documentos" element={<Documents />} /><Route path="contador" element={<MyAccountant />} /><Route path="calendario" element={<Calendar />} />
        <Route path="suporte" element={<Tickets />} /><Route path="assistente" element={<Assistant />} /><Route path="relatorios" element={<Reports />} />
        <Route path="assinatura" element={<Subscription />} /><Route path="configuracoes" element={<Settings />} />
      </Route>
      <Route path="/contador" element={<Gate><ContadorArea /></Gate>}>
        <Route index element={<ContadorDashboard />} /><Route path="empresas" element={<ContadorCompanies />} />
        <Route path="documentos" element={<TabbedPage title="Documentos Recebidos" tabs={[["arquivos", "Arquivos", <DocumentList title="Arquivos da empresa" />], ["solicitar", "Solicitar documentos", REQUESTS]]} />} />
        <Route path="apuracoes" element={<TabbedPage title="Apurações" subtitle="Estimativas e apurações para conferência e aprovação." tabs={[["a", "Apurações", ASSESSMENTS]]} />} />
        <Route path="notas" element={<Invoices />} /><Route path="obrigacoes" element={<Calendar />} />
        <Route path="guias" element={<TabbedPage title="Guias de Pagamento" subtitle="Anexe guias verdadeiras geradas em sistemas oficiais." tabs={[["g", "Guias", GUIDES]]} />} />
        <Route path="fechamento" element={<TabbedPage title="Fechamento Mensal" tabs={[["f", "Competências", PERIODS]]} />} />
        <Route path="escrituracao" element={<DocumentList only="demonstrativo_contabil" uploadCategory="demonstrativo_contabil" title="Escrituração e demonstrativos" subtitle="Publique balancetes, DRE, balanço e livros gerados no sistema contábil." />} />
        <Route path="folha" element={<TabbedPage title="Folha de Pagamento" tabs={[["s", "Solicitações", PAYROLL], ["f", "Funcionários", EMPLOYEES]]} />} />
        <Route path="atendimento" element={<Tickets title="Atendimento" categories={["contador"]} />} /><Route path="relatorios" element={<Reports />} />
        <Route path="configuracoes" element={<OrgSettings />} />
      </Route>
      <Route path="/admin" element={<Gate><AdminArea /></Gate>}>
        <Route index element={<A.AdminDashboard />} /><Route path="empresas" element={<A.AdminCompanies />} /><Route path="usuarios" element={<A.AdminUsers />} />
        <Route path="parceiros" element={<A.AdminPartners />} /><Route path="planos" element={<A.AdminPlans />} /><Route path="assinaturas" element={<A.AdminSubscriptions />} />
        <Route path="integracoes" element={<A.AdminIntegrations />} /><Route path="notificacoes" element={<A.AdminNotifications />} /><Route path="suporte" element={<A.AdminSupport />} />
        <Route path="marca" element={<A.AdminBrand />} /><Route path="conteudo" element={<A.AdminContent />} /><Route path="auditoria" element={<A.AdminAudit />} />
        <Route path="seguranca" element={<A.AdminSecurity />} /><Route path="relatorios" element={<A.AdminReports />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <SiteProvider>
          <SessionProvider>
            <AppRoutes />
            <Toaster position="top-center" richColors />
          </SessionProvider>
        </SiteProvider>
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}
